/**
 * Resolución del contenido de archivos de una carpeta, sin Firestore encima.
 *
 * Contexto: en la vista clásica las filas de archivo salen de los docs-puntero
 * de la subcolección de la carpeta. Cada puntero solo trae
 * `{ reference: files/{id} }` — sin nombre ni fecha — así que hasta ahora el
 * listado hacía una lectura EN SERIE por fila (`list_folders.js:522`): 85
 * archivos = 85 lecturas encadenadas = ~7 s antes de pintar.
 *
 * Dos hechos permiten quitar casi todas esas lecturas:
 *
 * 1. La entrada canónica del archivo (nombre, url, tipo, fecha) ya vive en el
 *    array `files[]` del doc padre, que `getFolderInfo` (`list_folders.js:119`)
 *    **ya leyó** para resolver permisos. El id del puntero es el `fileId` de
 *    esa entrada, así que cruzarlos es un join en memoria: 0 lecturas.
 * 2. El id del puntero es `Archivo No - {Date.now()}-{rnd}` (`upload.js:18`).
 *    El timestamp va primero y tiene 13 dígitos fijos, así que su orden
 *    lexicográfico ES el orden de subida. Eso da un orden estable del listado
 *    completo sin conocer un solo nombre — y por eso se puede hidratar solo la
 *    página visible sin que las filas salten de lugar.
 *
 * Módulo puro: sin React, sin Redux, sin importar firebase. Las referencias
 * llegan por parámetro (basta que expongan `.get()`).
 */

import { mergeFolderFiles } from './folder_files';

// Tamaño de tanda de lecturas paralelas. 20 es el valor ya probado en
// bulk_tree.js y bulk_accounting.js; se mantiene igual en todo el proyecto.
export const FILE_HYDRATION_BATCH = 20;

/**
 * Índice fileId → entrada canónica. La primera gana ante repetidos (mismo
 * criterio que mergeFolderFiles).
 * @param {object[]|undefined} entries
 * @returns {Map<string, object>}
 */
export const indexByFileId = (entries) => {
  const idx = new Map();
  (Array.isArray(entries) ? entries : []).forEach((e) => {
    if (e && e.fileId && !idx.has(e.fileId)) idx.set(e.fileId, e);
  });
  return idx;
};

/**
 * Separa los docs de la subcolección hija en docs self de carpeta (traen
 * `folders[]`) y docs-puntero de archivo (traen `reference`). Un doc que no es
 * ninguno se descarta: dato corrupto o parcial.
 * @param {object[]|undefined} docs  DocumentSnapshot[] del SDK v8
 */
export const splitFolderDocs = (docs) => {
  const folderDocs = [];
  const pointerDocs = [];
  (Array.isArray(docs) ? docs : []).forEach((doc) => {
    const data = typeof doc?.data === 'function' ? doc.data() : null;
    if (!data) return;
    if (Array.isArray(data.folders)) folderDocs.push(doc);
    else if (data.reference) pointerDocs.push(doc);
  });
  return { folderDocs, pointerDocs };
};

/**
 * Una fila por doc-puntero. `entry` viene del array cuando hay cruce (0
 * lecturas) y queda en null cuando el archivo es legacy y hay que leerlo.
 *
 * Los punteros son la AUTORIDAD sobre qué archivos pertenecen a esta carpeta:
 * el `files[]` del doc padre está compartido entre carpetas hermanas
 * (off-by-one de upload.js:80), así que se usa solo como fuente de metadatos,
 * filtrado por los punteros presentes.
 *
 * @param {object}   params
 * @param {object[]} params.pointerDocs   docs-puntero (de splitFolderDocs)
 * @param {object[]} params.arrayEntries  files[] ya fusionado con files-store
 * @returns {object[]} FileRow[] — { fileId, ref, entry, full, failed }
 */
export const buildFileRows = ({ pointerDocs, arrayEntries }) => {
  const idx = indexByFileId(arrayEntries);
  const rows = [];
  (Array.isArray(pointerDocs) ? pointerDocs : []).forEach((doc) => {
    const ref = doc.data()?.reference;
    if (!ref || typeof ref.get !== 'function') return; // puntero corrupto
    rows.push({
      fileId: doc.id,
      ref,
      entry: idx.get(doc.id) || null,
      full: null,
      failed: false,
    });
  });
  return rows;
};

/**
 * Ordena las filas. No muta.
 *  - 'recent': por el id del puntero, descendente. Es el orden de subida y no
 *    exige conocer los nombres, así que funciona desde el primer pintado.
 *  - 'alpha': por nombre (es-CO). Las filas sin nombre van al final; para que
 *    este orden sea completo hay que hidratar toda la carpeta.
 * @param {object[]} rows
 * @param {'recent'|'alpha'} mode
 */
export const sortFileRows = (rows, mode) => {
  const out = (Array.isArray(rows) ? rows : []).slice();
  if (mode === 'alpha') {
    return out.sort((a, b) => {
      const na = a?.entry?.name;
      const nb = b?.entry?.name;
      if (!na && !nb) return 0;
      if (!na) return 1;
      if (!nb) return -1;
      return na.localeCompare(nb, 'es');
    });
  }
  return out.sort((a, b) => String(b?.fileId || '').localeCompare(String(a?.fileId || '')));
};

/**
 * ¿Falta algún nombre? Es la condición para poder ordenar A-Z o filtrar en
 * memoria. Una fila que ya falló no cuenta: reintentarla dejaría el orden A-Z
 * bloqueado para siempre en una carpeta con un archivo roto.
 * @param {object[]} rows
 */
export const needsFullHydration = (rows) =>
  (Array.isArray(rows) ? rows : []).some(r => !r?.entry && !r?.failed);

/**
 * Lee `files/{id}` de las filas que aún no tienen `full` y emite cada fila
 * completa en cuanto llega. Leer el doc completo es deliberado: es justo lo que
 * necesita el drawer de detalle, así que después el clic no cuesta nada.
 *
 * Una lectura que falla marca su fila (`failed: true`) en vez de romper el
 * bucle. Antes un `.get()` rechazado dejaba la carpeta vacía.
 *
 * `reads` es el instrumento de verificación de los criterios de aceptación: la
 * consola de red del navegador no sirve para contar lecturas de Firestore
 * porque el SDK las multiplexa sobre WebChannel.
 *
 * @param {object}   params
 * @param {object[]} params.rows       FileRow[] — se filtran las que ya tienen full
 * @param {number}   [params.batchSize]
 * @param {function} params.onRow      (row) => void, con entry y full resueltos
 * @param {function} [params.onFailure] ({ fileId, error }) => void
 * @returns {Promise<{ reads: number, failed: number }>}
 */
export const hydrateRows = async ({
  rows, batchSize = FILE_HYDRATION_BATCH, onRow, onFailure,
}) => {
  const list = (Array.isArray(rows) ? rows : []).filter(r => r && !r.full && !r.failed);
  let reads = 0;
  let failed = 0;

  // Definida fuera del bucle a propósito: un closure declarado dentro que
  // mutara los contadores dispara no-loop-func. Devuelve si falló y el bucle
  // hace la cuenta.
  const readOne = async (row) => {
    try {
      const snap = await row.ref.get();
      if (!snap || snap.exists === false) {
        onRow({ ...row, failed: true });
        if (onFailure) onFailure({ fileId: row.fileId, error: new Error('doc inexistente') });
        return true;
      }
      const data = snap.data();
      onRow({ ...row, entry: data, full: data, failed: false });
      return false;
    } catch (error) {
      onRow({ ...row, failed: true });
      if (onFailure) onFailure({ fileId: row.fileId, error });
      return true;
    }
  };

  for (let i = 0; i < list.length; i += batchSize) {
    const batch = list.slice(i, i + batchSize);
    // eslint-disable-next-line no-await-in-loop
    const fallos = await Promise.all(batch.map(readOne));
    reads += batch.length;
    failed += fallos.filter(Boolean).length;
  }

  return { reads, failed };
};

/**
 * Actualiza una fila en sitio, sin recargar la carpeta. Devuelve un arreglo
 * nuevo; no muta.
 *
 * Los campos del patch se aplican tanto a `entry` (lo que pinta la fila) como
 * a `full` (lo que recibe el drawer), para que ambos queden coherentes sin
 * volver a leer nada. Es lo que permite que marcar contabilizado o adjuntar un
 * soporte refresquen su etiqueta sin recargar el listado entero.
 *
 * @param {object[]} rows
 * @param {string}   fileId
 * @param {object}   patch  campos a sobreescribir (ej. { accounted, hasAccounting })
 */
export const patchFileRow = (rows, fileId, patch) =>
  (Array.isArray(rows) ? rows : []).map(r => (
    !r || r.fileId !== fileId ? r : {
      ...r,
      entry: r.entry ? { ...r.entry, ...patch } : r.entry,
      full: r.full ? { ...r.full, ...patch } : r.full,
    }
  ));

/**
 * Quita una fila del listado. Devuelve un arreglo nuevo; no muta.
 * @param {object[]} rows
 * @param {string}   fileId
 */
export const removeFileRow = (rows, fileId) =>
  (Array.isArray(rows) ? rows : []).filter(r => r && r.fileId !== fileId);

/**
 * Entradas del array de una carpeta, fusionando `files[]` con los docs de
 * `files-store` cuando la carpeta está en overflow.
 * @param {object|undefined} folderData  data() del doc que tiene el array
 * @param {object[]|undefined} storeDocs data() de cada doc de files-store
 */
export const folderArrayEntries = (folderData, storeDocs) =>
  mergeFolderFiles(folderData?.files, storeDocs);
